import {
  Box,
  Button,
  Chip,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { cloneDeep, first } from "lodash-es";
import { useCallback, useEffect, useRef, useState } from "react";

import { ChatMessageNodeBuilder } from "@/common/builders/chatMessageNode";
import AppIconButton from "@/common/components/Button/AppIconButton";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { ChatContextEventsMap, useChatContext } from "@/common/contexts/chat";
import { ChatMessageHelper } from "@/common/helpers/entity/chatMessage";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ValidationHelper, ValidationInfo } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  ChatMessageDto,
  ChatMessageNodeDto,
  ChatMessageRootNodeDto,
  ChatTipsDto,
} from "@/core/api/generated/v1";

import { ChatSize } from "../../Chat/ChatDisplay";
import ChatMessageTagHandler from "./ChatMessageTagHandler";
import ChatMessageWebLinkHandler from "./ChatMessageWebLinkHandler";

export interface ChatMessageInputFeaturesProps {
  mentions?: boolean;
  tips?: boolean;
}

const defaultFeaturesProps: ChatMessageInputFeaturesProps = {
  mentions: true,
  tips: true,
};

export interface ChatMessageInputProps {
  chatId: string;
  initialValue?: ChatMessageDto;
  size?: ChatSize;
  placeholder?: string;
  actionText?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  maxLength?: number;
  validation?: ValidationInfo;
  hasUploadedAttachments?: boolean;
  secondaryActions?: React.ReactNode[];
  features?: ChatMessageInputFeaturesProps;
  defaultEnableCancel?: boolean;
  renderAttachments?: () => React.ReactNode;
  onFocus?: TextFieldProps["onFocus"];
  onBlur?: TextFieldProps["onBlur"];
  onChange?: (data: { body: string; rootNode: ChatMessageRootNodeDto }) => void;
  onPasteFiles?: (files: File[]) => void;
  onAction?: (data: { body: string; rootNode: ChatMessageRootNodeDto }) => void | Promise<void>;
  onCancel?: () => void;
}

export default function ChatMessageInput({
  chatId,
  initialValue,
  size = "medium",
  placeholder = "Reply...",
  actionText = "Save",
  autoFocus = false,
  disabled = false,
  maxLength = 1000,
  validation,
  hasUploadedAttachments,
  secondaryActions,
  features,
  defaultEnableCancel = false,
  renderAttachments,
  onFocus,
  onBlur,
  onChange,
  onPasteFiles,
  onAction,
  onCancel,
}: ChatMessageInputProps) {
  features = {
    ...defaultFeaturesProps,
    ...features,
  };

  const { enqueueSnackbar } = useAppSnackbar();
  const { hasPermissions } = useAuthorizationInfo();
  const chatContext = useChatContext();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [messageBody, setMessageBody] = useState("");
  const [rootNode, setRootNode] = useState<ChatMessageRootNodeDto>({
    nodes: [],
  });
  const [plainInputError, setPlainInputError] = useState<string | undefined>(undefined);
  const [tips, setTips] = useState<ChatTipsDto | undefined>(undefined);

  const currentSelectionRef = useRef<CustomCursorSelection>({
    startIndex: null,
    endIndex: null,
  });
  const showActions = true;
  const canPerformPrimaryAction = hasUploadedAttachments || (messageBody && messageBody.length > 0);
  const isNotEmptyMessageEntered = messageBody && messageBody.length > 0;

  const scrollToInput = useCallback(() => {
    inputRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [inputRef.current]);
  const focusInput = useCallback(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 500);
  }, [inputRef.current]);

  // handle context events
  useEffect(() => {
    const handleScrollToInputRequested = () => {
      console.log("handleScrollToInputRequested.");
      scrollToInput();
    };

    const handleScrollToInputAndFocusRequested = () => {
      console.log("handleScrollToInputAndFocusRequested.");
      scrollToInput();
      focusInput();
    };

    const handleCreateTagByScopeInMessageInputRequested = async (
      params: ChatContextEventsMap["createTagByScopeInMessageInputRequested"],
    ) => {
      console.log("handleCreateTagInMessageInputRequested.", { params });

      // find tag candidate by scope and add to message
      try {
        const response = await apiClient.generalTagsApi.apiV1TagsGeneralCandidatesGetByScopePost({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          generalTagCandidatesByScopeRequestDto: {
            offset: 0,
            limit: 100,
            scope: params.scope || undefined,
          },
        });
        const tagCandidate = first(response.data.items);
        const tagSubCandidate = first(tagCandidate?.subCandidates);

        const tagNodes: ChatMessageNodeDto[] = [];
        if (tagCandidate) {
          tagNodes.push(ChatMessageNodeBuilder.newTagNodeFromCandidate(tagCandidate).build());
        }
        if (tagSubCandidate) {
          tagNodes.push(ChatMessageNodeBuilder.newTagNodeFromCandidate(tagSubCandidate).build());
        }

        if (tagNodes.length !== 0) {
          const rootNode2 = cloneDeep(rootNode);

          // white space from entered text
          if (rootNode2.nodes!.length !== 0) {
            ChatMessageHelper.insertNodeAtTheEnd(
              rootNode2,
              ChatMessageNodeBuilder.newTextNodeForWhiteSpace().build(),
            );
          }

          tagNodes.forEach((node) => {
            ChatMessageHelper.insertNodeAtTheEnd(rootNode2, node);
            ChatMessageHelper.insertNodeAtTheEnd(
              rootNode2,
              ChatMessageNodeBuilder.newTextNodeForWhiteSpace().build(),
            );
          });

          setRootNode(rootNode2);
          setMessageBody(ChatMessageHelper.buildRootNodeFullText(rootNode2));
        }
      } catch (err) {
        const validation2 = ValidationHelper.handleApiErrorResponse(err);
        validation2.hasErrors &&
          enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
      }

      if (params.isScrollToInput) {
        scrollToInput();
      }
      if (params.isFocusInput) {
        focusInput();
      }
    };

    chatContext.on("scrollToMessageInputRequested", handleScrollToInputRequested);
    chatContext.on("scrollToMessageInputAndFocusRequested", handleScrollToInputAndFocusRequested);
    chatContext.on(
      "createTagByScopeInMessageInputRequested",
      handleCreateTagByScopeInMessageInputRequested,
    );

    return () => {
      chatContext.off("scrollToMessageInputRequested", handleScrollToInputRequested);
      chatContext.off(
        "scrollToMessageInputAndFocusRequested",
        handleScrollToInputAndFocusRequested,
      );
      chatContext.off(
        "createTagByScopeInMessageInputRequested",
        handleCreateTagByScopeInMessageInputRequested,
      );
    };
  }, [rootNode]);

  useEffect(() => {
    if (initialValue) {
      const initialRootNode = initialValue.rootNode
        ? cloneDeep(initialValue.rootNode)
        : {
            nodes: [],
          };
      setRootNode(initialRootNode);
      setMessageBody(initialValue.body || "");
    }
  }, [initialValue]);

  const reset = () => {
    setIsActionLoading(false);
    setRootNode({
      nodes: [],
    });
    setMessageBody("");
  };

  const handleAction = async () => {
    if (onAction) {
      if (messageBody.trim().length === 0) {
        setMessageBody("");
        return;
      }
      const finalRootNode = ChatMessageHelper.buildFinalRootNode(rootNode, { resetTempIds: true });
      setIsActionLoading(true);
      try {
        await onAction({
          body: messageBody,
          rootNode: finalRootNode,
        });
        reset();
        inputRef.current?.focus();
      } finally {
        setIsActionLoading(false);
      }
    } else {
      reset();
    }
  };

  // const handleCancel = () => {
  //   setMessageBody("");
  //   setRootNode({ nodes: [] });
  //   onCancel && onCancel();
  // };

  const handleTips = useCallback(async () => {
    try {
      const response = await apiClient.chatsApi.apiV1ChatsChatIdTipsGet({
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        chatId,
      });
      if (response?.data && response.data.tips?.length !== 0) {
        setTips(response.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  return (
    <Box>
      <ChatMessageTagHandler
        disabled={!features.mentions || !hasPermissions([AppPermission.FleetAppAccess])}
        onMessageChange={(newValue) => {
          // console.log("onMessageChange (tag)", {
          //   body: ChatMessageHelper.buildRootNodeFullText(rootNode),
          //   rootNode: newValue.rootNode,
          //   nodesText: rootNode.nodes!.map((x) => x.text?.text),
          // });
          setRootNode(newValue.rootNode);
          setMessageBody(ChatMessageHelper.buildRootNodeFullText(rootNode));
        }}
      >
        {(tagParams) => (
          <ChatMessageWebLinkHandler
            onMessageChange={(newValue) => {
              // console.log("onMessageChange (weblink)", {
              //   body: ChatMessageHelper.buildRootNodeFullText(rootNode),
              //   rootNode: newValue.rootNode,
              //   nodesText: rootNode.nodes!.map((x) => x.text?.text),
              // });
              setRootNode(newValue.rootNode);
              setMessageBody(ChatMessageHelper.buildRootNodeFullText(rootNode));
            }}
          >
            {(webLinkParams) => (
              <Stack>
                {/* AI tips */}
                {tips && (
                  <Box sx={{ mb: 1 }}>
                    {tips.tips?.map((tip, i) => (
                      <Button
                        key={i}
                        onClick={() => {
                          if (!tip.body) {
                            return;
                          }

                          setMessageBody(tip.body);
                          setRootNode({
                            nodes: [
                              ChatMessageNodeBuilder.newTextNode()
                                .withText({ text: tip.body })
                                .build(),
                            ],
                          });
                          currentSelectionRef.current = {
                            startIndex: tip.body.length,
                            endIndex: tip.body.length,
                          };
                          onChange && onChange({ body: tip.body, rootNode: rootNode });
                          setTips(undefined);
                        }}
                        sx={{ p: 0 }}
                      >
                        <Chip
                          color='primary'
                          label={tip.body}
                          sx={{
                            whiteSpace: "nowrap",
                            width: "min-content",
                            m: 0.5,
                          }}
                        />
                      </Button>
                    ))}
                  </Box>
                )}

                <Box
                  sx={{
                    border: (t) =>
                      `1px solid ${isNotEmptyMessageEntered ? t.palette.secondary.main : t.palette.divider}`,
                    borderRadius: (t) => t.shapeCustom.borderRadiusCard,
                    // pl: 1,
                    overflow: "hidden",
                  }}
                >
                  <Stack direction='row' gap={0}>
                    {/* Secondary actions */}
                    {/* {showActions && (
                      <Stack direction='row' gap={1} sx={{ mr: "auto", alignItems: "center" }}>
                        {secondaryActions?.map((secondaryAction, i) => (
                          <Box key={i}>{secondaryAction}</Box>
                        ))}
                      </Stack>
                    )} */}

                    {/* Text input */}
                    <TextField
                      sx={{
                        "& .MuiInputBase-root": {
                          backgroundColor: "transparent !important",
                          boxShadow: "none !important",

                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none !important",
                          },
                        },
                      }}
                      inputRef={inputRef}
                      slotProps={{
                        input: {
                          startAdornment: showActions && (secondaryActions || features.tips) && (
                            <InputAdornment sx={{ alignSelf: "start" }} position='start'>
                              <Stack
                                direction='row'
                                gap={0.25}
                                sx={{ mr: "auto", alignItems: "center" }}
                              >
                                {/* Secondary actions */}
                                {secondaryActions?.map((secondaryAction, i) => (
                                  <Box key={i} component='span'>
                                    {secondaryAction}
                                  </Box>
                                ))}

                                {features.tips && !tips && (
                                  <AppTooltip title='Get AI tips'>
                                    <AppIconButton size='small' onClick={handleTips}>
                                      <AppIcon of='sparkles' />
                                    </AppIconButton>
                                  </AppTooltip>
                                )}
                                {features.tips && tips && (
                                  <AppTooltip title='Close AI tips'>
                                    <AppIconButton size='small' onClick={() => setTips(undefined)}>
                                      <AppIcon of='clear' />
                                    </AppIconButton>
                                  </AppTooltip>
                                )}
                              </Stack>
                            </InputAdornment>
                          ),
                          endAdornment: showActions && (
                            <InputAdornment sx={{ alignSelf: "start" }} position='end'>
                              <Stack
                                direction='row'
                                gap={0.25}
                                sx={{ mr: "auto", alignItems: "center" }}
                              >
                                {/* Clear message */}
                                {/* {defaultEnableCancel ||
                                  (isNotEmptyMessageEntered && (
                                    <AppIconButton size='small' onClick={handleCancel}>
                                      <AppIcon of='clear' />
                                    </AppIconButton>
                                  ))} */}

                                <Tooltip
                                  sx={{ height: "100%", display: "flex" }}
                                  enterDelay={300}
                                  enterNextDelay={300}
                                  title={
                                    <Stack>
                                      {/* <Box>{actionText}</Box> */}
                                      <Typography component='div'>
                                        <Box>Enter or Ctrl + Enter to send</Box>
                                        <Box>Shift + Enter to add new line</Box>
                                      </Typography>
                                    </Stack>
                                  }
                                >
                                  <AppIconButton
                                    sx={{
                                      // borderRadius: 0,
                                      // p: 1.5,
                                      ...((!isNotEmptyMessageEntered && {
                                        backgroundColor: (th) => th.palette.background.paper,
                                      }) || {
                                        backgroundColor: (t) =>
                                          `${t.palette.primary.main} !important`,
                                        color: (t) => t.palette.primary.contrastText,
                                      }),
                                      "&:hover": {
                                        ...((!isNotEmptyMessageEntered && {
                                          backgroundColor: (th) => th.palette.background.paper,
                                        }) || {
                                          backgroundColor: (t) =>
                                            `${t.palette.primary.main} !important`,
                                          color: (t) => t.palette.primary.contrastText,
                                        }),
                                      },
                                    }}
                                    // size={
                                    //   (size === "small" && "small") ||
                                    //   (size === "medium" && "medium") ||
                                    //   "large"
                                    // }
                                    size='medium'
                                    shape='square'
                                    circularProgressSize={18}
                                    disableRipple
                                    // variant='contained'
                                    color={isNotEmptyMessageEntered ? "primary" : "text"}
                                    disabled={!canPerformPrimaryAction}
                                    loading={isActionLoading}
                                    onClick={handleAction}
                                  >
                                    <AppIcon of='send' fontSize='small' color='inherit' />
                                  </AppIconButton>
                                  {/*<Stack sx={{ width: "50px", backgroundColor: "red" }}></Stack>*/}
                                </Tooltip>
                              </Stack>
                            </InputAdornment>
                          ),
                        },
                      }}
                      fullWidth
                      multiline
                      maxRows={6}
                      autoFocus={autoFocus}
                      disabled={disabled}
                      label=''
                      placeholder={placeholder}
                      variant='outlined'
                      size={size || "medium"}
                      value={messageBody}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      onChange={(e) => {
                        if (plainInputError) {
                          setPlainInputError(undefined);
                        }
                        const newValue = e.target.value;
                        if (newValue.length > maxLength) {
                          const errMessage = `Max input value length of ${maxLength} exceeded.`;
                          setPlainInputError(errMessage);
                          throw new Error(errMessage);
                        }

                        const newSelection: CustomCursorSelection = {
                          startIndex: e.target.selectionStart,
                          endIndex: e.target.selectionEnd,
                        };

                        setMessageBody(newValue);

                        onChange && onChange({ body: newValue, rootNode: rootNode });
                        let handleResult = tagParams.handleMessageChange(
                          rootNode,
                          currentSelectionRef.current,
                          newSelection,
                          newValue,
                        );
                        if (!handleResult.isHandled) {
                          handleResult = webLinkParams.handleMessageChange(
                            rootNode,
                            currentSelectionRef.current,
                            newSelection,
                            newValue,
                          );
                        }

                        if (!handleResult.isHandled) {
                          ChatMessageHelper.handleTextChange({
                            rootNode,
                            oldSelection: currentSelectionRef.current,
                            newSelection,
                            change: {
                              value: newValue,
                            },
                          });
                          setRootNode(rootNode);
                          setMessageBody(ChatMessageHelper.buildRootNodeFullText(rootNode));
                        }

                        // console.log("onChange.", {
                        //   value: e.target.value,
                        //   body: ChatMessageHelper.buildRootNodeFullText(rootNode),
                        //   handleResult,
                        //   nodeIndexes: rootNode.nodes!.map((x) => ({
                        //     startIndex: x.startIndex,
                        //     endIndex: x.endIndex,
                        //   })),
                        //   currentSelection: currentSelectionRef.current,
                        //   newSelection,
                        //   rootNode,
                        //   nodesText: rootNode.nodes!.map((x) => x.text?.text),
                        // });
                      }}
                      onKeyDown={(e) => {
                        let result = tagParams.handleKeyDown(e);
                        if (!result.isHandled) {
                          result = webLinkParams.handleKeyDown(e);
                        }

                        if (result.isHandled) {
                          e.preventDefault();
                          return;
                        }
                        if (e.code === "Enter" && e.ctrlKey) {
                          e.preventDefault(); // prevent new line
                          handleAction();
                        }
                        if (e.code === "Enter" && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                          e.preventDefault(); // prevent new line
                          handleAction();
                        }
                      }}
                      // fires on text selection (via mouse or keyboard, after focus),
                      // fires after onChange
                      onSelect={(e) => {
                        type TargetType = HTMLTextAreaElement | HTMLInputElement;
                        const event = e as React.KeyboardEvent<TargetType>;
                        currentSelectionRef.current = {
                          startIndex: (event.target as TargetType).selectionStart,
                          endIndex: (event.target as TargetType).selectionEnd,
                        };
                      }}
                      onPaste={(e) => {
                        const files = Array.from(e.clipboardData.files || []);
                        if (files.length !== 0 && onPasteFiles) {
                          e.preventDefault();
                          onPasteFiles(files);
                        }
                      }}
                    />

                    {/* Primary actions */}
                    {/* {showActions && (
                      <Tooltip
                        sx={{ height: "100%", display: "flex" }}
                        enterDelay={300}
                        enterNextDelay={300}
                        title={
                          <Stack>
                            <Box>{actionText}</Box>
                            <Typography component='div' variant='caption'>
                              CTRL + ENTER to submit
                            </Typography>
                          </Stack>
                        }
                      >
                        <AppIconButton
                          sx={{
                            // borderRadius: 0,
                            // p: 1.5,
                            ...((!isNotEmptyMessageEntered && {
                              backgroundColor: (th) => th.palette.background.paper,
                            }) || {
                              backgroundColor: (t) => `${t.palette.primary.main} !important`,
                              color: (t) => t.palette.primary.contrastText,
                            }),
                            "&:hover": {
                              ...((!isNotEmptyMessageEntered && {
                                backgroundColor: (th) => th.palette.background.paper,
                              }) || {
                                backgroundColor: (t) => `${t.palette.primary.main} !important`,
                                color: (t) => t.palette.primary.contrastText,
                              }),
                            },
                          }}
                          size={
                            (size === "small" && "small") ||
                            (size === "medium" && "medium") ||
                            "large"
                          }
                          shape='square'
                          disableRipple
                          // variant='contained'
                          color={isNotEmptyMessageEntered ? "primary" : "text"}
                          disabled={!canPerformPrimaryAction}
                          loading={isActionLoading}
                          onClick={handleAction}
                        >
                          <AppIcon of='send' color='inherit' />
                        </AppIconButton>
                      </Tooltip>
                    )} */}
                  </Stack>
                </Box>

                {validation?.hasErrors && (
                  <FormHelperText error={validation?.hasErrors} component='div'>
                    {validation?.getErrorsAsList().map((errMessage, i) => (
                      <Typography component='div' key={i} fontSize='inherit'>
                        {errMessage}
                      </Typography>
                    ))}
                  </FormHelperText>
                )}

                {plainInputError && (
                  <FormHelperText error component='div'>
                    <Typography component='div' fontSize='inherit'>
                      {plainInputError}
                    </Typography>
                  </FormHelperText>
                )}
              </Stack>
            )}
          </ChatMessageWebLinkHandler>
        )}
      </ChatMessageTagHandler>

      {/* Attachments */}
      {renderAttachments && (
        <Box sx={{ mt: 1, maxHeight: "300px", overflowY: "auto" }}>{renderAttachments()}</Box>
      )}
    </Box>
  );
}
